import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Award, Building2, Clock, Quote, CheckCircle2, Loader2, Target, Lightbulb, TrendingUp } from "lucide-react";
import PageHeader from "../components/PageHeader";
import ContactCTA from "../components/ContactCTA";
import { caseStudiesApi } from "../lib/api";

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    caseStudiesApi.get(slug).then(setC).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="lis-container py-32 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin"/> Loading case study...</div>;
  if (notFound || !c) {
    return (
      <div className="lis-container py-32 text-center">
        <h1 className="text-2xl font-bold">Case study not found</h1>
        <Link to="/case-studies" className="btn-brand mt-6 inline-flex">Back to case studies</Link>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={c.title}
        subtitle={c.outcome_summary}
        crumbs={[{ label: "Case Studies", to: "/case-studies" }, { label: c.standard || "Story" }]}
      />

      <section className="section-pad bg-white">
        <div className="lis-container grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Link to="/case-studies" className="text-sm text-brand font-semibold inline-flex items-center gap-1 mb-4"><ArrowLeft size={14}/> All case studies</Link>
            <div className="flex flex-wrap gap-3 mb-4">
              {c.standard && <span className="chip"><Award size={12}/> {c.standard}</span>}
              {c.industry && <span className="chip"><Building2 size={12}/> {c.industry}</span>}
              {c.duration && <span className="chip"><Clock size={12}/> {c.duration}</span>}
            </div>
            {c.image && (
              <img src={c.image} alt={c.title} className="w-full h-72 md:h-96 object-cover rounded-2xl shadow-lg" />
            )}

            <div className="mt-8 flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-red-50 text-brand flex items-center justify-center"><Target size={22}/></div>
              <h2 className="text-2xl font-bold text-slate-900">The Challenge</h2>
            </div>
            <div className="divider-red mt-3" />
            <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line">{c.challenge}</p>

            <div className="mt-10 flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-red-50 text-brand flex items-center justify-center"><Lightbulb size={22}/></div>
              <h2 className="text-2xl font-bold text-slate-900">Our Solution</h2>
            </div>
            <div className="divider-red mt-3" />
            <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line">{c.solution}</p>

            <div className="mt-10 flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-red-50 text-brand flex items-center justify-center"><TrendingUp size={22}/></div>
              <h2 className="text-2xl font-bold text-slate-900">The Outcome</h2>
            </div>
            <div className="divider-red mt-3" />
            <p className="mt-4 text-slate-700 leading-relaxed">{c.outcome_summary}</p>

            {c.outcomes?.length > 0 && (
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {c.outcomes.map((o, i) => (
                  <div key={i} className="p-5 rounded-xl border border-slate-100 bg-slate-50">
                    <div className="text-2xl font-extrabold text-brand">{o.value}</div>
                    <div className="text-sm text-slate-600 mt-1">{o.label}</div>
                  </div>
                ))}
              </div>
            )}

            {c.quote && (
              <div className="mt-10 p-6 md:p-8 rounded-2xl bg-slate-900 text-white relative">
                <Quote size={28} className="text-brand opacity-70" />
                <p className="mt-3 text-lg leading-relaxed">“{c.quote}”</p>
                <div className="mt-5 text-sm">
                  <div className="font-semibold">{c.quote_author}</div>
                  <div className="text-slate-400">{c.quote_role}</div>
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-brand">Discuss your project <ArrowRight size={16}/></Link>
              <Link to="/case-studies" className="btn-outline">More success stories</Link>
            </div>
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-slate-100 p-6">
              <div className="text-xs uppercase tracking-widest text-slate-500">Client</div>
              <div className="mt-1 font-semibold text-slate-900">{c.client_display_name || c.company || "Confidential"}</div>
              {c.company && <div className="text-xs text-slate-500 mt-0.5">{c.company}</div>}
              <div className="mt-4 space-y-3 text-sm">
                {c.industry && (
                  <div className="flex items-start gap-2">
                    <Building2 size={16} className="text-brand mt-0.5"/>
                    <div><div className="text-xs text-slate-500">Industry</div><div className="text-slate-800">{c.industry}</div></div>
                  </div>
                )}
                {c.standard && (
                  <div className="flex items-start gap-2">
                    <Award size={16} className="text-brand mt-0.5"/>
                    <div><div className="text-xs text-slate-500">Standard</div><div className="text-slate-800">{c.standard}</div></div>
                  </div>
                )}
                {c.duration && (
                  <div className="flex items-start gap-2">
                    <Clock size={16} className="text-brand mt-0.5"/>
                    <div><div className="text-xs text-slate-500">Timeline</div><div className="text-slate-800">{c.duration}</div></div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-2xl p-6 bg-slate-900 text-white">
              <div className="text-xs uppercase tracking-widest text-slate-400">Talk to us</div>
              <h4 className="mt-1 text-xl font-bold">Your certification story next</h4>
              <p className="mt-2 text-sm text-slate-300">Get a free scoping call for your ISO or compliance programme.</p>
              <Link to="/contact" className="btn-brand mt-5 w-full justify-center">Book a Consultation <ArrowRight size={14}/></Link>
            </div>
          </aside>
        </div>
      </section>

      <ContactCTA />
    </>
  );
};

export default CaseStudyDetailPage;
